import type { FetchError } from 'ofetch';
import type { OrderPayload, OrderSearchPayload, OrderAddresses, OrderIdResponse } from './types';
import type { HttpClient, HttpResponse } from '@/utils/HttpClient';

type ErrorReponse = {
  response?: {
    status: number;
  };
};

export type OrderService = {
  downloadOrderAsExcel: (params: OrderPayload) => Promise<unknown>;
  downloadOrderAsPDF: (params: OrderPayload) => Promise<unknown>;
  getInvoices: <T>(orderId: string) => Promise<T>;
  sendOrderDocumentsEmail: <T>(orderId: string, payload: unknown) => Promise<T>;
  search: <T>(payload: OrderSearchPayload, limit: number, offset: number) => Promise<T>;
  sendOrderRequest: (payload: unknown) => Promise<unknown>;
  getAddresses: (orderId: string, token: string, baseUrl: string) => Promise<HttpResponse<OrderAddresses, FetchError>>;
  getOrderId: (temporaryOrderId: string) => Promise<HttpResponse<OrderIdResponse, FetchError>>;
};

export const OrderService = (httpClient: HttpClient) => {
  return {
    downloadOrderAsExcel({ orderId }: OrderPayload) {
      return httpClient({ endpoint: `/orders/${orderId}/download` });
    },
    downloadOrderAsPDF({ orderId }: OrderPayload) {
      return httpClient({ endpoint: `/orders/${orderId}/documents/order-confirmation` });
    },
    getInvoices(orderId: string) {
      return httpClient({ endpoint: `/orders/${orderId}/invoices` });
    },
    getAddresses(orderId: string) {
      return httpClient({ endpoint: `/orders/${orderId}/addresses` });
    },
    sendOrderDocumentsEmail(orderId: string, payload: { from: string; subject: string; html: string; to: string[] }) {
      return httpClient<{ data: unknown }, ErrorReponse>({
        endpoint: `/orders/${orderId}/documents/email`,
        method: 'POST',
        body: { message: payload },
        requestOptions: { omitErrorMessage: true },
      });
    },
    search(payload: OrderSearchPayload, limit: number, offset: number) {
      let query = `?limit=${limit}&offset=${offset}`;
      const allowedParams = ['q', 'orderStatus', 'fromDate', 'toDate', 'sort'];
      for (const key in payload) {
        if (!allowedParams.includes(key)) {
          continue;
        }
        query += `&${key}=${payload[key]}`;
      }

      return httpClient({ endpoint: `/orders/search${query}` });
    },
    sendOrderRequest(payload: unknown) {
      return httpClient({ endpoint: `/orders/inquiry`, method: 'POST', body: payload });
    },
    getOrderId(temporaryOrderId: string): Promise<HttpResponse<OrderIdResponse, FetchError>> {
      return httpClient({ endpoint: `/orders/getOrderId/${temporaryOrderId}`, method: 'GET' });
    },
  };
};
